import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Button, Row, Col, Form, Collapse, Modal } from "react-bootstrap";
import { FiTrash2, FiChevronDown } from "react-icons/fi";
import { FaGripVertical, FaPen, FaEye } from "react-icons/fa";
import { FormControl as MUIFormControl, InputLabel, Select as MUISelect, MenuItem, FormControlLabel, Checkbox } from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import { Subtask, Subject, Topic, Subtopic, DeleteTarget, SUBTASK_TYPE } from "./ProjectConfigurationTypes";
import { SUBTASK_TYPES, SubtaskTypes, CODING_EDITOR_TYPES, ACTION_TYPE } from "./projectConfigConstants";
import ProjectCodingEditor from "./ProjectCodingEditor";
import ProjectCodingView from "./ProjectCodingView";
import CommonContentList from "./CommonContentList";
import SubtopicInfoTable from "./SubtopicInfoTable";
import ContentStatusIndicator from "./ContentStatusIndicator";
import authService from "../../../utils/authService";
import { fetchContentCreatorsWithCache } from "../../../utils/ccUtils";
import { PiMonitorPlayBold } from "react-icons/pi";
import { SlNotebook } from "react-icons/sl";
import { BsListTask } from "react-icons/bs";
import { LiaLaptopCodeSolid } from "react-icons/lia";
import { FaCode } from "react-icons/fa";
type PreviewContent =
| { type: typeof SUBTASK_TYPE.VIDEO; otp: string; playback_info: string }
| { type: typeof SUBTASK_TYPE.NOTES; content: string };

/**
 * Content Status Type Definition
 * Exported for use in subcomponents
 */
export type ContentStatus = {
  [SUBTASK_TYPE.VIDEO]?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
  [SUBTASK_TYPE.NOTES]?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
  [SUBTASK_TYPE.MCQ]?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
  [SUBTASK_TYPE.CODING]?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
  [SUBTASK_TYPE.PROJECT_CODING]?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
};


/**
 * Common Filters Component
 * Displays Subject, Topic, Sub Topic, and Subtask Type dropdowns
 * These filters are constant for all 5 subtask types
 */
const CommonFilters: React.FC<{
  subtask: Subtask;
  subjects: Subject[];
  availableTopics: Topic[];
  availableSubtopics: Subtopic[];
  isAssignMode: boolean;
  isSubtaskRestricted?: boolean;
  onSubjectChange: (event: SelectChangeEvent<string>) => void;
  onTopicChange: (event: SelectChangeEvent<string>) => void;
  onSubtopicChange: (event: SelectChangeEvent<string>) => void;
  onSubtaskTypeChange: (event: SelectChangeEvent<string>) => void;
}> = ({
  subtask,
  subjects,
  availableTopics,
  availableSubtopics,
  isAssignMode,
  isSubtaskRestricted = false,
  onSubjectChange,
  onTopicChange,
  onSubtopicChange,
  onSubtaskTypeChange
}) => {
  return (
    <Row className="g-3">
      <Col md={3}>
        <MUIFormControl variant="outlined" size="small" fullWidth>
          <InputLabel>Subject</InputLabel>
          <MUISelect
            value={subtask.subjectId}
            onChange={onSubjectChange}
            label="Subject"
            disabled={isSubtaskRestricted}
          >
            <MenuItem value="">Select Subject</MenuItem>
            {Array.isArray(subjects) && subjects.map(subject => (
              <MenuItem key={subject.subject_id} value={subject.subject_id}>
                {subject.subject_name}
              </MenuItem>
            ))}
          </MUISelect>
        </MUIFormControl>
      </Col>
      <Col md={3}>
        <MUIFormControl variant="outlined" size="small" fullWidth>
          <InputLabel>Topic</InputLabel>
          <MUISelect
            value={subtask.topicId}
            onChange={onTopicChange}
            label="Topic"
            disabled={!subtask.subjectId || isSubtaskRestricted}
          >
            <MenuItem value="">Select Topic</MenuItem>
            {Array.isArray(availableTopics) && availableTopics.map(topic => (
              <MenuItem key={topic.topic_id} value={topic.topic_id}>
                {topic.topic_name}
              </MenuItem>
            ))}
          </MUISelect>
        </MUIFormControl>
      </Col>
      <Col md={3}>
        <MUIFormControl variant="outlined" size="small" fullWidth>
          <InputLabel>Sub Topic</InputLabel>
          <MUISelect
            value={subtask.subtopicId}
            onChange={onSubtopicChange}
            label="Sub Topic"
            disabled={!subtask.topicId || isSubtaskRestricted}
          >
            <MenuItem value="">Select Sub Topic</MenuItem>
            {Array.isArray(availableSubtopics) && availableSubtopics.map(subtopic => (
              <MenuItem key={subtopic.subtopic_id} value={subtopic.subtopic_id}>
                {subtopic.subtopic_name}
              </MenuItem>
            ))}
          </MUISelect>
        </MUIFormControl>
      </Col>
      <Col md={3}>
        <MUIFormControl variant="outlined" size="small" fullWidth>
          <InputLabel>Subtask Type</InputLabel>
          <MUISelect
            value={subtask.subtaskType}
            onChange={onSubtaskTypeChange}
            label="Subtask Type"
            disabled={(isAssignMode && !!subtask.subtaskType) || isSubtaskRestricted}
          >
            <MenuItem value="">Select Type</MenuItem>
            {SUBTASK_TYPES.map(type => (
              <MenuItem key={type.type} value={type.type}>
                {type.label}
              </MenuItem>
            ))}
          </MUISelect>
        </MUIFormControl>
      </Col>
    </Row>
  );
};

/**
 * Coding Editor Filter Component
 * Displays Coding Editor Type dropdown (only for coding type)
 */
const CodingEditorFilter: React.FC<{
  subtask: Subtask;
  isAssignMode: boolean;
  isEditMode: boolean;
  hasExistingContent: boolean;
  isSubtaskRestricted?: boolean;
  onEditorChange: (event: SelectChangeEvent<string>) => void;
}> = ({
  subtask,
  isAssignMode,
  isEditMode,
  hasExistingContent,
  isSubtaskRestricted = false,
  onEditorChange
}) => {
  // Only show for coding type in edit mode
  if (subtask.subtaskType !== SUBTASK_TYPE.CODING || (!isEditMode && hasExistingContent)) {
    return null;
  }

  return (
    <Col md={3}>
      <MUIFormControl variant="outlined" size="small" fullWidth>
        <InputLabel>Coding Editor Type</InputLabel>
        <MUISelect
          value={subtask.editor || ""}
          onChange={onEditorChange}
          label="Coding Editor Type"
          disabled={(isAssignMode && !subtask.subtaskType) || isSubtaskRestricted}
        >
          <MenuItem value="">Select Editor Type</MenuItem>
          {CODING_EDITOR_TYPES && Array.isArray(CODING_EDITOR_TYPES) && CODING_EDITOR_TYPES.map(editor => (
            <MenuItem key={editor.type} value={editor.type}>
              {editor.label}
            </MenuItem>
          ))}
        </MUISelect>
      </MUIFormControl>
    </Col>
  );
};

/**
 * Determines when to show the Level dropdown
 * @param isAssignMode - Whether the subtask is in assign mode
 * @param isSelectMode - Whether the subtask is in select mode
 * @param subtaskType - The type of subtask
 * @returns true if level dropdown should be shown, false otherwise
 */
const shouldShowLevelDropdown = (isAssignMode: boolean, isSelectMode: boolean, subtaskType: string): boolean => {
  // Show level dropdown if:
  // 1. In select mode (for all types), OR
  // 2. Not in assign mode, OR
  // 3. In assign mode but subtask type is NOT MCQ or CODING
  // Note: If subtaskType is empty/undefined, show dropdown only in select mode or when not in assign mode
  if (!subtaskType || subtaskType.trim() === "") {
    return isSelectMode || !isAssignMode;
  }
  return isSelectMode || !isAssignMode || (subtaskType !== SUBTASK_TYPE.MCQ && subtaskType !== SUBTASK_TYPE.CODING);
};

/**
 * Level Dropdown Component
 * Displays the Level dropdown with conditional rendering
 */
const LevelDropdown: React.FC<{
  subtask: Subtask;
  isAssignMode: boolean;
  isSelectMode: boolean;
  contentCreated: boolean;
  isSubtaskRestricted?: boolean;
  onContentLevelChange: (level: string) => void;
  onAddContent?: () => void;
  isAddingContent?: boolean;
  contentCreators?: Array<{ user_id: string; user_name: string }>;
  onCcChange?: (ccId: string) => void;
}> = ({
  subtask,
  isAssignMode,
  isSelectMode,
  contentCreated,
  isSubtaskRestricted = false,
  onContentLevelChange,
  onAddContent,
  isAddingContent,
  contentCreators = [],
  onCcChange
}) => {
  if (!shouldShowLevelDropdown(isAssignMode, isSelectMode, subtask.subtaskType)) {
    return null;
  }

  const isProjectCoding = subtask.subtaskType === SUBTASK_TYPE.PROJECT_CODING;
  const showCcDropdown = isProjectCoding && isAssignMode && contentCreators.length > 0;

  return (
    <Row className="g-3 mb-3">
      <Col md={3}>
        <MUIFormControl variant="outlined" size="small" fullWidth>
          <InputLabel>Level</InputLabel>
          <MUISelect
            value={subtask.selectedContentLevel || ""}
            onChange={(e) => onContentLevelChange(e.target.value)}
            label="Level"
            disabled={isSubtaskRestricted}
          >
            <MenuItem value="">Select Level</MenuItem>
            <MenuItem value="level1">Level 1</MenuItem>
            <MenuItem value="level2">Level 2</MenuItem>
            <MenuItem value="level3">Level 3</MenuItem>
          </MUISelect>
        </MUIFormControl>
      </Col>
      {showCcDropdown && (
        <Col md={3}>
          <MUIFormControl variant="outlined" size="small" fullWidth>
            <InputLabel>Content Creator</InputLabel>
            <MUISelect
              value={((subtask as any)?.cc_id && String((subtask as any).cc_id).trim()) ? String((subtask as any).cc_id).trim() : ""}
              onChange={(e) => {
                const ccId = e.target.value && String(e.target.value).trim() ? String(e.target.value).trim() : "";
                if (onCcChange && ccId) {
                  onCcChange(ccId);
                }
              }}
              label="Content Creator"
              disabled={isSubtaskRestricted}
            >
              {contentCreators.map((cc) => (
                <MenuItem key={cc.user_id} value={cc.user_id}>
                  {cc.user_name}
                </MenuItem>
              ))}
            </MUISelect>
          </MUIFormControl>
        </Col>
      )}
      {isAssignMode && subtask.selectedContentLevel && onAddContent && (
        <Col md={showCcDropdown ? 2 : 3} className="d-flex align-items-center justify-content-center">
          <Button
            variant="primary"
            size="sm"
            onClick={onAddContent}
            disabled={!subtask.subtopicId || !subtask.selectedContentLevel || contentCreated || isAddingContent || (showCcDropdown && !((subtask as any)?.cc_id && String((subtask as any).cc_id).trim())) || isSubtaskRestricted}
            style={{ height: "40px", width: "100px" }}
          >
            {isAddingContent ? (
              <>
                Creating...
              </>
            ) : (
              "Add Content"
            )}
          </Button>
        </Col>
      )}
    </Row>
  );
};

  // Helper function to get content type icon
  const getSubtaskTypeIcon = (
    subtaskType: SubtaskTypes
  ): React.ReactElement => {
    switch (subtaskType) {
      case SUBTASK_TYPE.VIDEO:
        return <PiMonitorPlayBold size={20} className="text-primary me-2"/>;
      case SUBTASK_TYPE.NOTES:
        return <SlNotebook size={20} className="text-primary me-2"/>;
      case SUBTASK_TYPE.MCQ:
        return <BsListTask size={20} className="text-primary me-2"/>;
      case SUBTASK_TYPE.CODING:
        return <LiaLaptopCodeSolid size={20} className="text-primary me-2"/>;
      case SUBTASK_TYPE.PROJECT_CODING:
        return <FaCode size={20} className="text-primary me-2"/>;
      default:
        return <></>;
    }
  };

/**
 * Gets all question IDs from level1, level2, and level3 arrays
 * IMPORTANT: This function is for View/Assign mode ONLY. It reads ONLY from level arrays.
 * DO NOT use selectedContentId here - that's reserved for Select mode.
 * @param subtask - The subtask object containing level arrays
 * @returns Array of all question IDs as strings
 */
const getAllQuestionIds = (subtask: any): string[] => {
  const allIds: string[] = [];
  const l1 = subtask.level1 || [];
  const l2 = subtask.level2 || [];
  const l3 = subtask.level3 || [];
  if (Array.isArray(l1)) allIds.push(...l1.map((id: any) => String(id)));
  if (Array.isArray(l2)) allIds.push(...l2.map((id: any) => String(id)));
  if (Array.isArray(l3)) allIds.push(...l3.map((id: any) => String(id)));
  return allIds;
};

/**
 * Level Display Component - Renders MCQ/Coding questions for a specific level
 * Used in View mode. Determines which questions are selected based on mode:
 * - Select mode: Uses selectedContentId (latest selections)
 * - Assign/View mode: Uses level arrays (source of truth after save)
 */
const LevelQuestionsDisplay: React.FC<{
  levelKey: string;
  questions: any[];
  subtask: any;
  contentStatus?: ContentStatus;
}> = ({ levelKey, questions, subtask, contentStatus }) => {
  // Determine which IDs to use based on mode
  const isSelectMode = subtask.actionType === ACTION_TYPE.SELECT;
  let allIds: string[] = [];
  
  if (isSelectMode && subtask.selectedContentId?.trim()) {
    // Select mode: Use selectedContentId (latest selections, level arrays are stale)
    allIds = subtask.selectedContentId.split(",").filter((id: string) => id.trim());
  } else {
    // Assign/View mode: Use level arrays (source of truth after save)
    allIds = getAllQuestionIds(subtask);
  }
  const contentType = (subtask.subtaskType === SUBTASK_TYPE.MCQ ? SUBTASK_TYPE.MCQ : subtask.subtaskType === SUBTASK_TYPE.CODING ? SUBTASK_TYPE.CODING : subtask.subtaskType === SUBTASK_TYPE.PROJECT_CODING ? SUBTASK_TYPE.PROJECT_CODING : SUBTASK_TYPE.MCQ) as typeof SUBTASK_TYPE[keyof typeof SUBTASK_TYPE];

  return (
    <>
      {questions.map((question: any) => {
        const itemId = String(question.content_id);
        const isChecked = allIds.includes(itemId);

        return (
          <div key={question.content_id} className="d-flex mb-2 align-items-center">
            <div className="d-flex align-items-start p-2 border rounded flex-grow-1">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isChecked}
                    disabled
                  />
                }
                label={
                  <div style={{ marginLeft: "8px", fontSize: "12px", marginTop: "4px", color: "#000" }}>
                    {question.title || question.content_id}
                  </div>
                }
                sx={{
                  "& .MuiFormControlLabel-label": {
                    color: "#000 !important"
                  }
                }}
              />
            </div>
            <ContentStatusIndicator
              contentId={itemId}
              contentType={contentType}
              contentStatus={contentStatus}
            />
          </div>
        );
      })}
    </>
  );
};

interface SubtaskComponentProps {
  subtask: Subtask;
  phaseIndex: number;
  partIndex: number;
  taskIndex: number;
  subtaskIndex: number;
  subtaskId: string;
  taskId: string;
  isExpanded: boolean;
  projectId: string;
  subjects: Subject[];
  availableTopics: Topic[];
  availableSubtopics: Subtopic[];
  contentStatus?: ContentStatus | null;
  assignedCcIds?: string[] | null;
  dragHandleProps: any;
  onToggleExpand: () => void;
  onNameChange: (name: string) => void;
  allSubtasks?: Subtask[];
  onSelectOrAssign: (actionType: typeof ACTION_TYPE.SELECT | typeof ACTION_TYPE.ASSIGN) => void;
  onMandatoryChange: (checked: boolean) => void;
  onSubjectChange: (event: SelectChangeEvent<string>) => void;
  onTopicChange: (event: SelectChangeEvent<string>) => void;
  onSubtopicChange: (event: SelectChangeEvent<string>) => void;
  onSubtaskTypeChange: (event: SelectChangeEvent<string>) => void;
  onEditorChange: (event: SelectChangeEvent<string>) => void;
  onLevelChange: (event: SelectChangeEvent<string>) => void;
  onContentLevelChange: (level: string) => void;
  onContentCountChange: (level: string, count: string) => void;
  onAddContent: () => void;
  onFetchContent: (subtopicId: string) => Promise<void>;
  onContentSelected: (contentId: string, level: string, time: string) => void;
  onPreviewContent: (type: typeof SUBTASK_TYPE.VIDEO | typeof SUBTASK_TYPE.NOTES, content: PreviewContent) => void;
  onDelete: (target: DeleteTarget) => void;
  onContentCreated: (level1: string[], level2: string[], level3: string[]) => void;
  onDescriptionChange?: (description: string) => void;
  onCcChange?: (ccId: string) => void;
  isSubtaskRestricted?: boolean;
}

const SubtaskComponent: React.FC<SubtaskComponentProps> = ({
  subtask,
  phaseIndex,
  partIndex,
  taskIndex,
  subtaskIndex,
  subtaskId,
  taskId,
  isExpanded,
  projectId,
  subjects,
  availableTopics: availableTopicsProp,
  availableSubtopics: availableSubtopicsProp,
  contentStatus,
  assignedCcIds,
  dragHandleProps,
  onToggleExpand,
  onNameChange,
  onSelectOrAssign,
  onMandatoryChange,
  onSubjectChange,
  onTopicChange,
  onSubtopicChange,
  onSubtaskTypeChange,
  onEditorChange,
  onLevelChange,
  onContentLevelChange,
  onContentCountChange,
  onAddContent,
  onFetchContent,
  onContentSelected: onContentSelectedProp,
  onPreviewContent,
  onDelete,
  onContentCreated,
  onDescriptionChange,
  onCcChange,
  isSubtaskRestricted = false,
  allSubtasks = []
}) => {
  // Ensure availableTopics and availableSubtopics are always arrays
  const availableTopics = Array.isArray(availableTopicsProp) ? availableTopicsProp : [];
  const availableSubtopics = Array.isArray(availableSubtopicsProp) ? availableSubtopicsProp : [];
  
  // Ensure assignedCcIds is always an array (default to empty array if null/undefined)
  const safeAssignedCcIds = Array.isArray(assignedCcIds) ? assignedCcIds : [];
  
  // Check for duplicate subtask names within the task
  const isDuplicateSubtaskName = subtask.name && subtask.name.trim() !== "" && 
    allSubtasks.some((s, idx) => idx !== subtaskIndex && s.name && s.name.trim().toLowerCase() === subtask.name.trim().toLowerCase());
  const [loadingContent, setLoadingContent] = useState(false);
  const [mcqCodingContent, setMcqCodingContent] = useState<any>(null);
  const lastFetchedRef = useRef<{ subtopicId: string; subtaskType: string } | null>(null);
  
  // State for assign mode level counts (mcq/coding only)
  const [level1Count, setLevel1Count] = useState<string>("0");
  const [level2Count, setLevel2Count] = useState<string>("0");
  const [level3Count, setLevel3Count] = useState<string>("0");
  const [contentCreated, setContentCreated] = useState<boolean>(false);

  // Reset level counts and content cache when subject/topic/subtopic or subtask type changes (avoids stale selection UI)
  useEffect(() => {
    setLevel1Count("0");
    setLevel2Count("0");
    setLevel3Count("0");
    setMcqCodingContent(null);
    lastFetchedRef.current = null;
  }, [subtask.subjectId, subtask.topicId, subtask.subtopicId, subtask.subtaskType]);
  
  // State for project_coding description modal
  const [showDescriptionModal, setShowDescriptionModal] = useState<boolean>(false);
  const [descriptionText, setDescriptionText] = useState<string>("");
  
  // State for Add Content loading (for VIDEO/NOTES/PROJECT_CODING in assign mode)
  const [isAddingContent, setIsAddingContent] = useState<boolean>(false);
  
  // State for content creators (for PROJECT_CODING in assign mode)
  const [contentCreators, setContentCreators] = useState<Array<{ user_id: string; user_name: string }>>([]);
  
  // State for edit/view mode (only for MCQ and Coding)
  const isMcqCoding = subtask.subtaskType === SUBTASK_TYPE.MCQ || subtask.subtaskType === SUBTASK_TYPE.CODING;
  const isProjectCoding = subtask.subtaskType === SUBTASK_TYPE.PROJECT_CODING;
  const isVideoOrNotes = subtask.subtaskType === SUBTASK_TYPE.VIDEO || subtask.subtaskType === SUBTASK_TYPE.NOTES;
  
  // Load existing description from subtask
  useEffect(() => {
    if (isProjectCoding && (subtask as any).description) {
      setDescriptionText((subtask as any).description);
    }
  }, [isProjectCoding, (subtask as any).description]);
  
  // Fetch content creators for PROJECT_CODING in assign mode
  useEffect(() => {
    const fetchContentCreators = async () => {
      // Only fetch if: PROJECT_CODING, in assign mode, and has assigned CCs
      if (isProjectCoding && subtask.actionType === ACTION_TYPE.ASSIGN) {
        if (safeAssignedCcIds.length === 0) {
          setContentCreators([]);
          return;
        }
        
        try {
          const ccList = await fetchContentCreatorsWithCache();
          // Filter to show only assigned CCs
          const filtered = ccList.filter((cc: { user_id: string; user_name: string }) => 
            safeAssignedCcIds.includes(cc.user_id)
          );
          setContentCreators(filtered);
          
          // Default to first CC if not already set
          const currentCcId = (subtask as any)?.cc_id && String((subtask as any).cc_id).trim() ? String((subtask as any).cc_id).trim() : null;
          if (filtered.length > 0 && !currentCcId && onCcChange && filtered[0]?.user_id) {
            onCcChange(String(filtered[0].user_id).trim());
          }
        } catch (error) {
          console.error('Error fetching content creators:', error);
          setContentCreators([]);
        }
      } else {
        // Clear content creators if not PROJECT_CODING in assign mode
        setContentCreators([]);
      }
    };
    
    if (isProjectCoding && subtask.actionType === ACTION_TYPE.ASSIGN) {
      fetchContentCreators();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProjectCoding, subtask.actionType, safeAssignedCcIds]);
  
  // Clear loading state when content is created (for VIDEO/NOTES/PROJECT_CODING)
  useEffect(() => {
    if (contentCreated && isAddingContent) {
      setIsAddingContent(false);
    }
  }, [contentCreated, isAddingContent]);
  
  // Wrapper function for onAddContent with loading state management
  const handleAddContentClick = async () => {
    if (isAddingContent || contentCreated || isSubtaskRestricted) return;
    
    setIsAddingContent(true);
    try {
      if (onAddContent) {
        await Promise.resolve(onAddContent());
      }
      // Loading will be cleared when contentCreated changes
    } catch (error) {
      console.error('Error adding content:', error);
      setIsAddingContent(false);
    }
  };

  // Wrapper function for onContentSelected that checks restriction
  const handleContentSelected = (contentId: string, level: string, time: string) => {
    if (isSubtaskRestricted) return;
    onContentSelectedProp(contentId, level, time);
  };

  // Wrapper functions to prevent callbacks when restricted
  const handleNameChange = (name: string) => {
    if (isSubtaskRestricted) return;
    onNameChange(name);
  };

  const handleSelectOrAssign = (actionType: typeof ACTION_TYPE.SELECT | typeof ACTION_TYPE.ASSIGN) => {
    if (isSubtaskRestricted) return;
    onSelectOrAssign(actionType);
  };

  const handleMandatoryChange = (checked: boolean) => {
    if (isSubtaskRestricted) return;
    onMandatoryChange(checked);
  };

  const handleSubjectChange = (event: SelectChangeEvent<string>) => {
    if (isSubtaskRestricted) return;
    onSubjectChange(event);
  };

  const handleTopicChange = (event: SelectChangeEvent<string>) => {
    if (isSubtaskRestricted) return;
    onTopicChange(event);
  };

  const handleSubtopicChange = (event: SelectChangeEvent<string>) => {
    if (isSubtaskRestricted) return;
    onSubtopicChange(event);
  };

  const handleSubtaskTypeChange = (event: SelectChangeEvent<string>) => {
    if (isSubtaskRestricted) return;
    onSubtaskTypeChange(event);
  };

  const handleEditorChange = (event: SelectChangeEvent<string>) => {
    if (isSubtaskRestricted) return;
    onEditorChange(event);
  };

  const handleContentLevelChange = (level: string) => {
    if (isSubtaskRestricted) return;
    onContentLevelChange(level);
  };

  const handleCcChange = (ccId: string) => {
    if (isSubtaskRestricted) return;
    if (onCcChange) onCcChange(ccId);
  };

  const handleDescriptionChange = (description: string) => {
    if (isSubtaskRestricted) return;
    if (onDescriptionChange) onDescriptionChange(description);
  };
  
  /**
   * Calculates the total number of content items across all levels (level1, level2, level3)
   * For videos/notes, also checks selectedContentId
   * @returns Total count of content items in all level arrays
   */
  const getContentLength = () => {
    const l1 = (subtask as any).level1 || [];
    const l2 = (subtask as any).level2 || [];
    const l3 = (subtask as any).level3 || [];
    const levelCount = (Array.isArray(l1) ? l1.length : 0) + 
           (Array.isArray(l2) ? l2.length : 0) + 
           (Array.isArray(l3) ? l3.length : 0);
    
    // For videos/notes/project_coding, check if id exists
    if ((isVideoOrNotes || isProjectCoding) && subtask.id && String(subtask.id).trim()) {
      return 1;
    }
    
    // For MCQ/CODING in select mode: also check selectedContentId (before save, level arrays are empty)
    const isSelectMode = subtask.actionType === ACTION_TYPE.SELECT;
    if (isMcqCoding && isSelectMode && subtask.selectedContentId?.trim()) {
      const selectedIds = subtask.selectedContentId.split(",").filter((id: string) => id.trim());
      if (selectedIds.length > 0) {
        return selectedIds.length;
      }
    }
    
    return levelCount;
  };
  const hasExistingContent = getContentLength() > 0;
  const [isEditMode, setIsEditMode] = useState<boolean>(!hasExistingContent);
  
  // State for view mode data
  const [subtopicInfo, setSubtopicInfo] = useState<{ subtopic_name?: string; topic_name?: string; subject_name?: string } | null>(null);
  const [viewModeContent, setViewModeContent] = useState<any>(null);
  const [loadingViewData, setLoadingViewData] = useState(false);

  // Clear view-mode cache when subject/topic/subtopic or subtask type changes (avoids stale UI)
  useEffect(() => {
    setSubtopicInfo(null);
    setViewModeContent(null);
  }, [subtask.subjectId, subtask.topicId, subtask.subtopicId, subtask.subtaskType]);
  
  useEffect(() => {
    if (!isExpanded) {
      setIsEditMode(false);
    }
  }, [isExpanded]);

  // Prevent edit mode when subtask is restricted
  useEffect(() => {
    if (isSubtaskRestricted && isEditMode) {
      setIsEditMode(false);
    }
  }, [isSubtaskRestricted, isEditMode]);

  /**
   * Fetches subtopic information and content list for view mode display
   * IMPORTANT: Data source depends on mode:
   * - Select mode: Uses selectedContentId (contains latest selections, level arrays are stale)
   * - Assign/View mode: Uses level arrays (source of truth after save)
   * Step 1: Get subject name from subjects array
   * Step 2: Fetch topics and subtopics from API using subjectId
   * Step 3: Find topic and subtopic names from the fetched data
   * Step 4: For MCQ/Coding: Collect question IDs based on mode (selectedContentId for Select mode, level arrays for Assign/View mode)
   * Step 5: For MCQ/Coding: Fetch content list using the collected question IDs
   * Step 6: Store the fetched data in state for display
   */
  const fetchViewModeData = async () => {
    if (!subtask.subtopicId || !subtask.subjectId) return;
    
    try {
      setLoadingViewData(true);
      
      // Step 1: Get subject name from subjects array
      const subject = subjects.find(s => s.subject_id === subtask.subjectId);
      const subjectName = subject?.subject_name || "";
      
      // Step 2: Check sessionStorage first, then fetch topics and subtopics from API if needed
      let topicName = "";
      let subtopicName = "";
      
      const sessionKey = `topics_subtopics_${subtask.subjectId}`;
      let topics: Topic[] = [];
      
      // Check sessionStorage first
      const cachedData = sessionStorage.getItem(sessionKey);
      if (cachedData) {
        try {
          const parsedData = JSON.parse(cachedData);
          // Handle both array format (direct storage) and object format ({topics: [...]})
          topics = Array.isArray(parsedData) ? parsedData : (parsedData.topics || []);
        } catch (e) {
          console.error('Error parsing cached topics data:', e);
        }
      }
      
      // If not in sessionStorage, fetch from API
      if (topics.length === 0) {
        try {
          const response = await authService.post(`content_creation/topics_subtopics_by_subject/`, {
            subject_id: subtask.subjectId
          });
          
          topics = response.data.topics || [];
          
          // Store in sessionStorage for future use
          if (topics.length > 0) {
            sessionStorage.setItem(sessionKey, JSON.stringify(topics));
          }
        } catch (apiError) {
          console.error('Error fetching topics/subtopics:', apiError);
          // Fallback: Try to get from available props
          if (subtask.topicId && Array.isArray(availableTopics)) {
            const topic = availableTopics.find(t => t.topic_id === subtask.topicId);
            if (topic) {
              topicName = topic.topic_name || "";
              if (subtask.subtopicId && Array.isArray(availableSubtopics)) {
                const subtopic = availableSubtopics.find(st => st.subtopic_id === subtask.subtopicId);
                if (subtopic) {
                  subtopicName = subtopic.subtopic_name || "";
                }
              }
            }
          }
        }
      }
      
      // Step 3: Find topic and subtopic names from topics data
      if (topics.length > 0 && subtask.topicId && subtask.subtopicId) {
        const topic = topics.find((t: Topic) => t.topic_id === subtask.topicId);
        if (topic) {
          topicName = topic.topic_name || "";
          const subtopic = topic.sub_topics?.find((st: Subtopic) => st.subtopic_id === subtask.subtopicId);
          if (subtopic) {
            subtopicName = subtopic.subtopic_name || "";
          }
        }
      }
      
      // Fallback: If we still don't have names, try to get from available props
      if ((!topicName || !subtopicName) && subtask.topicId && Array.isArray(availableTopics)) {
        const topic = availableTopics.find(t => t.topic_id === subtask.topicId);
        if (topic && !topicName) {
          topicName = topic.topic_name || "";
        }
        if (subtask.subtopicId && !subtopicName && Array.isArray(availableSubtopics)) {
          const subtopic = availableSubtopics.find(st => st.subtopic_id === subtask.subtopicId);
          if (subtopic) {
            subtopicName = subtopic.subtopic_name || "";
          }
        }
      }
      
      // Set subtopic info
      setSubtopicInfo({
        subject_name: subjectName,
        topic_name: topicName,
        subtopic_name: subtopicName
      });
      
      // Step 4 & 5: Only fetch content list for MCQ/Coding types
      // CRITICAL: Data source depends on mode:
      // - Select mode: Use selectedContentId (latest selections, level arrays are stale)
      // - Assign/View mode: Use level arrays (source of truth after save)
      if (isMcqCoding) {
        const allQuestionIds: string[] = [];
        const isSelectMode = subtask.actionType === ACTION_TYPE.SELECT;
        
        if (isSelectMode && subtask.selectedContentId?.trim()) {
          // Select mode: Use selectedContentId as it contains the latest selections
          // Level arrays are not updated until save, so they're stale
          const selectedIds = subtask.selectedContentId.split(",").filter((id: string) => id.trim());
          allQuestionIds.push(...selectedIds);
        } else {
          // Assign/View mode: Use level arrays (source of truth after save)
          const l1 = (subtask as any).level1 || [];
          const l2 = (subtask as any).level2 || [];
          const l3 = (subtask as any).level3 || [];
          
          if (Array.isArray(l1)) allQuestionIds.push(...l1.map((id: any) => String(id)));
          if (Array.isArray(l2)) allQuestionIds.push(...l2.map((id: any) => String(id)));
          if (Array.isArray(l3)) allQuestionIds.push(...l3.map((id: any) => String(id)));
        }
        
        // Step 5 & 6: Fetch content list with question IDs and store in state
        if (allQuestionIds.length > 0) {
          const payload: any = {
            subtopic_id: subtask.subtopicId,
            content_type: subtask.subtaskType,
            question_ids: allQuestionIds
          };
          if (subtask.subtaskType === SUBTASK_TYPE.CODING) {
            payload.editor = subtask.editor || null;
          }
          const contentResponse = await authService.post('admin/content/list', payload);
          setViewModeContent(contentResponse.data);
        }
      }
      
      // Step 4 & 5: Fetch content list for PROJECT_CODING
      if (isProjectCoding) {
        // PROJECT_CODING: Use subtask.id (question ID)
        const questionId = subtask.id && String(subtask.id).trim() ? String(subtask.id).trim() : null;
        
        // Fetch content list with single question ID
        if (questionId) {
          const payload: any = {
            subtopic_id: subtask.subtopicId,
            content_type: subtask.subtaskType,
            question_ids: [questionId]
          };
          const contentResponse = await authService.post('admin/content/list', payload);
          setViewModeContent(contentResponse.data);
        }
      }
      
      setLoadingViewData(false);
    } catch (error) {
      console.error('Error fetching view mode data:', error);
      setLoadingViewData(false);
    }
  };
  
  /**
   * Fetches video preview data (otp and playback_info) from API
   * Step 1: Call student/videos/{video_id}/ API
   * Step 2: Check if otp and playback_info are empty strings
   * Step 3: If empty, call onPreviewContent with empty strings to show error message
   * Step 4: If valid, call onPreviewContent with the fetched data
   * @param videoId - The video ID to fetch preview data for
   */
  const handleVideoPreview = async (videoId: string) => {
    if (!videoId || !String(videoId).trim()) {
      console.warn('handleVideoPreview: Invalid videoId provided');
      return;
    }
    
    try {
      const response = await authService.get(`student/videos/${String(videoId).trim()}/`);
      if (response.data) {
        // Check if otp and playback_info are empty strings
        const hasEmptyData = (!response.data.otp || response.data.otp.trim() === "") || 
                            (!response.data.playback_info || response.data.playback_info.trim() === "");
        
        if (hasEmptyData) {
          // Pass empty strings to trigger error message in modal
          onPreviewContent(SUBTASK_TYPE.VIDEO, {
            type: SUBTASK_TYPE.VIDEO,
            otp: "",
            playback_info: ""
          });
        } else {
          onPreviewContent(SUBTASK_TYPE.VIDEO, {
            type: SUBTASK_TYPE.VIDEO,
            otp: response.data.otp,
            playback_info: response.data.playback_info
          });
        }
      }
    } catch (error) {
      console.error('Error fetching video preview:', error);
    }
  };

  /**
   * Fetches notes preview data (content) from API
   * Step 1: Call student/notes/{notes_id}/ API
   * Step 2: Check if content is empty string
   * Step 3: If empty, call onPreviewContent with empty string to show error message
   * Step 4: If valid, call onPreviewContent with the fetched data
   * @param notesId - The notes ID to fetch preview data for
   */
  const handleNotesPreview = async (notesId: string) => {
    if (!notesId || !String(notesId).trim()) {
      console.warn('handleNotesPreview: Invalid notesId provided');
      return;
    }
    
    try {
      const response = await authService.get(`student/notes/${String(notesId).trim()}/`);
      if (response.data) {
        // Check if content is empty string
        const hasEmptyData = !response.data.content || response.data.content.trim() === "";
        
        if (hasEmptyData) {
          // Pass empty string to trigger error message in modal
          onPreviewContent(SUBTASK_TYPE.NOTES, {
            type: SUBTASK_TYPE.NOTES,
            content: ""
          });
        } else {
          onPreviewContent(SUBTASK_TYPE.NOTES, {
            type: SUBTASK_TYPE.NOTES,
            content: response.data.content
          });
        }
      }
    } catch (error) {
      console.error('Error fetching notes preview:', error);
    }
  };

  const showContentSection =
  subtask.showDropdowns &&
  subtask.actionType &&
  [SUBTASK_TYPE.VIDEO, SUBTASK_TYPE.NOTES, SUBTASK_TYPE.MCQ, SUBTASK_TYPE.CODING, SUBTASK_TYPE.PROJECT_CODING].includes(subtask.subtaskType);

  const isSelectMode = subtask.actionType === ACTION_TYPE.SELECT;
  const isAssignMode = subtask.actionType === ACTION_TYPE.ASSIGN;

  /**
   * Effect: Fetches view mode data when component is expanded and in view mode
   * Triggers when: isExpanded, isEditMode, isMcqCoding, isVideoOrNotes, hasExistingContent, subtask.subtopicId, subtask.subtaskType, subtask.actionType, or subtask.selectedContentId changes
   * Condition: Only fetches if expanded, not in edit mode, has existing content, and subtopicId exists (for MCQ/Coding/Video/Notes)
   * Note: Includes actionType and selectedContentId in dependencies to refetch when mode changes or selections update
   */
  useEffect(() => {
    // Fetch view mode data when:
    // 1. Not in edit mode and has existing content (normal view mode), OR
    // 2. In assign mode for MCQ/Coding with existing content, OR
    // 3. In assign mode for PROJECT_CODING with existing content
    const shouldFetch = isExpanded && 
      subtask.subtopicId && 
      hasExistingContent &&
      (
        (isMcqCoding && (!isEditMode || isAssignMode)) ||
        (isVideoOrNotes && !isEditMode) ||
        (isProjectCoding && (!isEditMode || isAssignMode))
      );
    
    if (shouldFetch) {
      fetchViewModeData();
    }
  }, [isExpanded, isEditMode, isMcqCoding, isProjectCoding, isVideoOrNotes, hasExistingContent, subtask.subtopicId, subtask.subtaskType, subtask.actionType, subtask.selectedContentId, isAssignMode, (subtask as any).id, subtask.createdContent]);
  
  /**
   * Initializes content selection from a specific level array (level1, level2, or level3)
   * Step 1: Get the data array for the specified level
   * Step 2: If data exists, convert IDs to comma-separated string
   * Step 3: Update the selected content level and IDs via callbacks
   * @param level - The level key ("level1", "level2", or "level3")
   * @returns true if content was initialized, false otherwise
   */
  const initializeContentFromLevel = (level: string): boolean => {
    const levelData = (subtask as any)[level];
    if (Array.isArray(levelData) && levelData.length > 0) {
      // Step 1 & 2: Get level data and convert to comma-separated string
      const ids = levelData.map((id: any) => String(id)).join(",");
      // Step 3: Update state via callbacks
      handleContentLevelChange(level);
      handleContentSelected(ids, level, "");
      return true;
    }
    return false;
  };

  /**
   * Gets the currently selected content IDs for Select mode
   * IMPORTANT: This function is for Select mode ONLY. It prioritizes selectedContentId.
   * DO NOT use this in View/Assign mode - use getAllQuestionIds() instead which reads from level arrays.
   * Step 1: Check if selectedContentId exists and return split array (Select mode source of truth)
   * Step 2: If not, check selectedContentLevel and get IDs from corresponding level array (fallback)
   * Step 3: Return empty array if no selection found
   * @returns Array of selected content ID strings
   */
  const getSelectedIds = (): string[] => {
    // Step 1: Check selectedContentId first (Select mode source of truth)
    if (subtask.selectedContentId?.trim()) {
      return subtask.selectedContentId.split(",").filter((id: string) => id.trim());
    }
    
    // Step 2: Fallback to level arrays (for edge cases, not recommended)
    if (subtask.selectedContentLevel) {
      const levelData = (subtask as any)[subtask.selectedContentLevel];
      if (Array.isArray(levelData)) {
        return levelData.map((id: any) => String(id));
      }
    }
    
    // Step 3: Return empty if no selection
    return [];
  };

  /**
   * Effect: Handles content fetching and initialization when subtask is expanded
   * Step 1: Check if component is expanded and subtopicId exists
   * Step 2: For MCQ types in edit mode: Initialize content level and fetch content if needed
   * Step 3: For Coding types: Only initialize, don't fetch (will fetch on editor selection)
   * Step 4: For non-MCQ/Coding types: Fetch content via parent callback
   * Triggers when: isExpanded, subtask.subtopicId, subtask.subtaskType, subtask.selectedContentLevel, showContentSection, isEditMode, hasExistingContent, or isMcqCoding changes
   */
  useEffect(() => {
    // Step 1: Early return if not expanded or no subtopicId
    if (!isExpanded || !subtask.subtopicId) return;
    
    const isMcqCoding = subtask.subtaskType === SUBTASK_TYPE.MCQ || subtask.subtaskType === SUBTASK_TYPE.CODING;
    const isMcq = subtask.subtaskType === SUBTASK_TYPE.MCQ;
    const isCoding = subtask.subtaskType === SUBTASK_TYPE.CODING;
    const isProjectCoding = subtask.subtaskType === SUBTASK_TYPE.PROJECT_CODING;
    
    // Step 2: Handle MCQ/Coding types in edit mode
    if (isMcqCoding && (isEditMode || !hasExistingContent)) {
      // Auto-select first available level and initialize selectedContentId
      if (!subtask.selectedContentLevel) {
        // Try level1, then level2, then level3
        initializeContentFromLevel("level1") || 
        initializeContentFromLevel("level2") || 
        initializeContentFromLevel("level3");
      } else if (!subtask.selectedContentId) {
        // Initialize selectedContentId from existing level
        initializeContentFromLevel(subtask.selectedContentLevel);
      }
      
      // For MCQ: Fetch content if subtopicId or subtaskType changed
      // For Coding: Don't fetch here, will fetch when editor is selected
      if (isMcq) {
        const needsFetch = !lastFetchedRef.current || 
          lastFetchedRef.current.subtopicId !== subtask.subtopicId || 
          lastFetchedRef.current.subtaskType !== subtask.subtaskType;
        
        if (needsFetch) {
          setMcqCodingContent(null);
          fetchMcqCodingContent();
          lastFetchedRef.current = {
            subtopicId: subtask.subtopicId,
            subtaskType: subtask.subtaskType
          };
        }
      } else if (isCoding) {
        // For coding, only update the ref, don't fetch yet
        lastFetchedRef.current = {
          subtopicId: subtask.subtopicId,
          subtaskType: subtask.subtaskType
        };
      }
    } else if (isProjectCoding && (isEditMode || !hasExistingContent)) {
      // Step 2b: Handle PROJECT_CODING
      // Initialize level if not set
      if (!subtask.selectedContentLevel) {
        onContentLevelChange("level1");
      }
      // Fetch content for PROJECT_CODING
      const needsFetch = !lastFetchedRef.current || 
        lastFetchedRef.current.subtopicId !== subtask.subtopicId || 
        lastFetchedRef.current.subtaskType !== subtask.subtaskType;
      
      if (needsFetch) {
        setMcqCodingContent(null);
        fetchMcqCodingContent();
        lastFetchedRef.current = {
          subtopicId: subtask.subtopicId,
          subtaskType: subtask.subtaskType
        };
      }
    } else if (showContentSection && !isMcqCoding && !isProjectCoding) {
      // Step 3: For non-MCQ/Coding types, fetch content normally
      onFetchContent(subtask.subtopicId);
    }
  }, [isExpanded, subtask.subtopicId, subtask.subtaskType, subtask.selectedContentLevel, showContentSection, isEditMode, hasExistingContent, isMcqCoding]);

  /**
   * Effect: Fetches content list when editor is selected for coding type OR when entering select mode
   * Triggers when: subtask.editor changes for coding type OR when actionType changes to ACTION_TYPE.SELECT
   * Only fetches if there are questions assigned (IDs in level1, level2, or level3) OR in select mode
   * Note: All 3 levels are returned in the same API response, so we don't refetch on level change
   */
  useEffect(() => {
    // Trigger for coding type when:
    // 1. Editor is selected (existing behavior), OR
    // 2. Entering select mode (actionType changes to ACTION_TYPE.SELECT)
    const shouldTrigger = subtask.subtaskType === SUBTASK_TYPE.CODING && 
                          subtask.subtopicId && 
                          isExpanded &&
                          (subtask.editor || isSelectMode);
    
    if (shouldTrigger) {
      // In select mode, fetch without checking for questions (to show available content)
      // In assign/edit mode, only fetch if there are questions assigned
      if (isSelectMode) {
        setMcqCodingContent(null);
        fetchMcqCodingContent();
      } else {
        // Check if there are any questions assigned
        const level1Data = (subtask as any).level1 || [];
        const level2Data = (subtask as any).level2 || [];
        const level3Data = (subtask as any).level3 || [];
        
        const hasQuestions = (Array.isArray(level1Data) && level1Data.length > 0) ||
                            (Array.isArray(level2Data) && level2Data.length > 0) ||
                            (Array.isArray(level3Data) && level3Data.length > 0);
        
        // Only fetch if there are questions assigned
        if (hasQuestions) {
          setMcqCodingContent(null);
          fetchMcqCodingContent();
        }
      }
    }
  }, [subtask.editor, subtask.subtaskType, subtask.subtopicId, isExpanded, isSelectMode, (subtask as any).level1, (subtask as any).level2, (subtask as any).level3]);
  

  /**
   * Retrieves content items for the currently selected level, filtered by subtask type
   * Step 1: Determine content source based on subtask type (mcqCodingContent vs availableContent)
   * Step 2: Filter videos by level if subtask type is "video"
   * Step 3: Filter files by level if subtask type is "notes"
   * Step 4: Filter questions by level if subtask type is "mcq" or "coding"
   * Step 5: Return normalized content array with consistent structure
   * @returns Array of content items with normalized properties
   */
  const filteredContent = useMemo(() => {
    const isMcqCoding = subtask.subtaskType === SUBTASK_TYPE.MCQ || subtask.subtaskType === SUBTASK_TYPE.CODING || subtask.subtaskType === SUBTASK_TYPE.PROJECT_CODING;
    // Step 1: Determine content source
    const hasContent = isMcqCoding ? mcqCodingContent : subtask.availableContent;
    
    if (!hasContent || !subtask.selectedContentLevel) return [];
    
    const level = subtask.selectedContentLevel;
    const content: Array<{ content_id: string; text: string; time: string; level: string; type: typeof SUBTASK_TYPE[keyof typeof SUBTASK_TYPE]; playback_info?: string; otp?: string; content?: string }> = [];
    
    // Step 2: Filter videos by level
    if (subtask.subtaskType === SUBTASK_TYPE.VIDEO && subtask.availableContent && subtask.availableContent.videos) {
      Object.values(subtask.availableContent.videos).forEach(video => {
        if (video.level === level) {
          content.push({
            ...video,
            content_id: String(video.content_id),
            type: SUBTASK_TYPE.VIDEO
          });
        }
      });
    }
    
    // Step 3: Filter files by level
    if (subtask.subtaskType === SUBTASK_TYPE.NOTES && subtask.availableContent && subtask.availableContent.files) {
      Object.values(subtask.availableContent.files).forEach(file => {
        if (file.level === level) {
          content.push({
            ...file,
            content_id: String(file.content_id),
            type: SUBTASK_TYPE.NOTES
          });
        }
      });
    }

    // Step 4: Filter questions by level for MCQ/Coding/Project Coding
    if (
      (subtask.subtaskType === SUBTASK_TYPE.MCQ || subtask.subtaskType === SUBTASK_TYPE.CODING || subtask.subtaskType === SUBTASK_TYPE.PROJECT_CODING) &&
      mcqCodingContent
    ) {
      const levelKey = subtask.selectedContentLevel as string;
      const levelContent = mcqCodingContent.content[levelKey];
      
      if (Array.isArray(levelContent)) {
        levelContent.forEach((q: any) => {
          // Extract duration_minutes for PROJECT_CODING, convert to string, default to "0" if null/undefined
          let timeStr = "";
          if (subtask.subtaskType === SUBTASK_TYPE.PROJECT_CODING) {
            timeStr = q.duration_minutes != null ? String(q.duration_minutes) : "0";
          }
          
          content.push({
            content_id: q.content_id,
            text: q.title,
            level: subtask.selectedContentLevel || "",
            time: timeStr,
            type: subtask.subtaskType as typeof SUBTASK_TYPE[keyof typeof SUBTASK_TYPE]
          });
        });
      }
    }
    
    // Step 5: Return normalized content array
    return content;
  }, [
    subtask.subtaskType,
    subtask.selectedContentLevel,
    subtask.availableContent,
    mcqCodingContent
  ]);

  /**
   * Fetches MCQ or Coding content list from the API
   * Step 1: Validate that subtopicId and subtaskType are present
   * Step 2: Set loading state to true
   * Step 3: Make API call to fetch content list
   * Step 4: Store the response data in state if valid
   * Step 5: Set loading state to false
   */
  const fetchMcqCodingContent = async () => {
    // Step 1: Validation
    if (!subtask.subtopicId || !subtask.subtaskType) return;
    
    try {
      // Step 2: Set loading state
      setLoadingContent(true);
      const payload: any = {
        subtopic_id: subtask.subtopicId,
        content_type: subtask.subtaskType
      };
      if (subtask.subtaskType === SUBTASK_TYPE.CODING) {
        payload.editor = subtask.editor || null;
      }
      
      // Step 3: Fetch content from API
      const response = await authService.post('admin/content/list', payload);
      
      // Step 4: Store response data
      if (response.data && response.data.content) {
        setMcqCodingContent(response.data);
      }
      
      // Step 5: Reset loading state
      setLoadingContent(false);
    } catch (error) {
      console.error('Error fetching mcq/coding/project coding content:', error);
      setLoadingContent(false);
    }
  };

  /**
   * Creates blank content items for MCQ/Coding subtasks based on level counts
   * Step 1: Validate required parameters (projectId, subtopicId, subtaskType, taskId)
   * Step 2: Verify subtask type is MCQ or Coding
   * Step 3: Validate that at least one level count is greater than 0
   * Step 4: Build API payload with level counts
   * Step 5: Make API call to create blank content
   * Step 6: Extract created content IDs from response
   * Step 7: Merge new IDs with existing IDs for each level
   * Step 8: Update parent component with merged IDs via callback
   * Step 9: Reset level count inputs to "0"
   */
  const createBlankContent = async () => {
    // Step 0: Check if subtask is restricted
    if (isSubtaskRestricted) return;
    
    // Step 1: Validate required parameters
    if (!projectId || !subtask.subtopicId || !subtask.subtaskType || !taskId) return;
    
    // Step 2: Verify subtask type
    const isMcqCoding = subtask.subtaskType === SUBTASK_TYPE.MCQ || subtask.subtaskType === SUBTASK_TYPE.CODING;
    if (!isMcqCoding) return;
    
    // Step 3: Get existing counts
    const existingLevel1 = (subtask as any).level1 || [];
    const existingLevel2 = (subtask as any).level2 || [];
    const existingLevel3 = (subtask as any).level3 || [];
    
    const existingLevel1Count = Array.isArray(existingLevel1) ? existingLevel1.length : 0;
    const existingLevel2Count = Array.isArray(existingLevel2) ? existingLevel2.length : 0;
    const existingLevel3Count = Array.isArray(existingLevel3) ? existingLevel3.length : 0;
    
    // Step 4: Calculate difference (desired count - existing count)
    const desiredLevel1 = parseInt(level1Count) || 0;
    const desiredLevel2 = parseInt(level2Count) || 0;
    const desiredLevel3 = parseInt(level3Count) || 0;
    
    const diffLevel1 = Math.max(0, desiredLevel1 - existingLevel1Count);
    const diffLevel2 = Math.max(0, desiredLevel2 - existingLevel2Count);
    const diffLevel3 = Math.max(0, desiredLevel3 - existingLevel3Count);
    
    // Step 5: Validate at least one level has difference > 0
    if (diffLevel1 === 0 && diffLevel2 === 0 && diffLevel3 === 0) {
      return;
    }
    
    try {
      setLoadingContent(true);
      
      // Step 6: Build payload with difference counts
      const payload: any = {
        [taskId]: {
          subtopic_id: subtask.subtopicId,
          content: [
            {
              type: subtask.subtaskType,
              count: {
                level1: String(diffLevel1),
                level2: String(diffLevel2),
                level3: String(diffLevel3)
              },
              editor: subtask.subtaskType === SUBTASK_TYPE.CODING ? (subtask.editor || null) : null
            }
          ]
        }
      };
      
      // Step 7: Create blank content via API
      const response = await authService.post(`admin/content/create_blank_content/${projectId}/`, payload);
      
      // Step 8: Extract created IDs from response
      if (response.data && response.data[taskId]?.content?.[0]?.count) {
        const countData = response.data[taskId].content[0].count;
        const level1Ids = countData.level1 || [];
        const level2Ids = countData.level2 || [];
        const level3Ids = countData.level3 || [];
        
        // Step 9: Merge with existing IDs
        const mergedLevel1 = [...existingLevel1, ...level1Ids];
        const mergedLevel2 = [...existingLevel2, ...level2Ids];
        const mergedLevel3 = [...existingLevel3, ...level3Ids];
        
        // Step 10: Update parent component
        onContentCreated(mergedLevel1, mergedLevel2, mergedLevel3);
        
        // Step 11: Collect all question IDs from merged arrays
        const allQuestionIds: string[] = [];
        if (Array.isArray(mergedLevel1)) allQuestionIds.push(...mergedLevel1.map((id: any) => String(id)));
        if (Array.isArray(mergedLevel2)) allQuestionIds.push(...mergedLevel2.map((id: any) => String(id)));
        if (Array.isArray(mergedLevel3)) allQuestionIds.push(...mergedLevel3.map((id: any) => String(id)));
        
        // Step 12: Fetch updated content list with all question IDs
        if (allQuestionIds.length > 0) {
          try {
            const listPayload: any = {
              subtopic_id: subtask.subtopicId,
              content_type: subtask.subtaskType,
              question_ids: allQuestionIds
            };
            if (subtask.subtaskType === SUBTASK_TYPE.CODING) {
              listPayload.editor = subtask.editor || null;
            }
            const contentResponse = await authService.post('admin/content/list', listPayload);
            if (contentResponse.data && contentResponse.data.content) {
              // Update both states to ensure UI updates in both edit and view modes
              setMcqCodingContent(contentResponse.data);
              setViewModeContent(contentResponse.data);
            }
          } catch (contentError) {
            console.error('Error fetching updated content list:', contentError);
          }
        }
        
        // Step 13: Update input fields to reflect the new total counts (desired counts)
        setLevel1Count(String(desiredLevel1));
        setLevel2Count(String(desiredLevel2));
        setLevel3Count(String(desiredLevel3));
      }
      
      setLoadingContent(false);
    } catch (error) {
      console.error('Error creating blank content:', error);
      setLoadingContent(false);
    }
  };

  /**
   * Effect: Checks if content exists and updates contentCreated state, pre-fills level counts for assign mode
   * Step 1: Check all possible sources for existing content (level arrays, selectedContentId, createdContent)
   * Step 2: Update contentCreated state based on content existence
   * Step 3: For MCQ/Coding assign mode, pre-fill level count inputs with existing counts
   * Triggers when: subtask.subtaskType, subtask.actionType, level arrays, selectedContentId, or createdContent changes
   */
  useEffect(() => {
    const level1Data = (subtask as any).level1;
    const level2Data = (subtask as any).level2;
    const level3Data = (subtask as any).level3;
    
    // Step 1: Check all sources for existing content
    const isVideoNotesProjectCoding = subtask.subtaskType === SUBTASK_TYPE.VIDEO || subtask.subtaskType === SUBTASK_TYPE.NOTES || subtask.subtaskType === SUBTASK_TYPE.PROJECT_CODING;
    const hasContentId = isVideoNotesProjectCoding ? !!(subtask.id && String(subtask.id).trim()) : !!(subtask.selectedContentId && String(subtask.selectedContentId).trim());
    const hasContent = !!(Array.isArray(level1Data) && level1Data.length > 0) ||
                      !!(Array.isArray(level2Data) && level2Data.length > 0) ||
                      !!(Array.isArray(level3Data) && level3Data.length > 0) ||
                      hasContentId ||
                      !!(subtask.createdContent && Array.isArray(subtask.createdContent) && subtask.createdContent.length > 0);
    
    // Step 2: Update contentCreated state
    setContentCreated(hasContent);
    
    // Step 3: Pre-fill level counts for MCQ/Coding assign mode
    if ((subtask.subtaskType === SUBTASK_TYPE.MCQ || subtask.subtaskType === SUBTASK_TYPE.CODING) && isAssignMode) {
      if (Array.isArray(level1Data) && level1Data.length > 0) {
        setLevel1Count(String(level1Data.length));
      }
      if (Array.isArray(level2Data) && level2Data.length > 0) {
        setLevel2Count(String(level2Data.length));
      }
      if (Array.isArray(level3Data) && level3Data.length > 0) {
        setLevel3Count(String(level3Data.length));
      }
    }
  }, [subtask.subtaskType, subtask.actionType, (subtask as any).level1, (subtask as any).level2, (subtask as any).level3, subtask.selectedContentId, subtask.id, subtask.createdContent, isAssignMode]);

  return (
    <div className="mb-2 border rounded p-2 bg-light">
      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-2 flex-grow-1">
          <div {...dragHandleProps} style={{ cursor: isSubtaskRestricted ? 'not-allowed' : 'grab', display: 'flex', alignItems: 'center', opacity: isSubtaskRestricted ? 0.5 : 1 }}>
            <FaGripVertical />
            
          </div>
          <Button
            variant="light"
            size="sm"
            className="p-1"
            onClick={onToggleExpand}
            style={{ minWidth: "28px" }}
          >
            <FiChevronDown
              size={16}
              style={{
                transition: "transform 0.2s",
                transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)"
              }}
            />
          </Button>
          <span className="fw-bold me-2" style={{ fontSize: "13px" }}>{getSubtaskTypeIcon(subtask.subtaskType as SubtaskTypes)} {subtask.subtask_id ? subtask.subtask_id.charAt(0).toUpperCase() + subtask.subtask_id.slice(1) : subtask.subtask_id}</span>
          <div>
            <Form.Control
              type="text"
              placeholder="Subtask Name"
              value={subtask.name}
              onChange={(e) => handleNameChange(e.target.value)}
              style={{ maxWidth: "200px", height: "32px" }}
              size="sm"
            />
            {isDuplicateSubtaskName && (
              <div className="text-warning mt-2">
                Subtask name has already been used.
              </div>
            )}
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
        {(isAssignMode && isProjectCoding) && (
                        <FaEye 
                          size={14}  
                          onClick={() => {
                            if (!isSubtaskRestricted) {
                              setDescriptionText((subtask as any)?.description || "");
                              setShowDescriptionModal(true);
                            }
                          }}
                          title="Edit Description" 
                          className="align-items-center mx-1"
                          style={{ cursor: isSubtaskRestricted ? 'not-allowed' : 'pointer', opacity: isSubtaskRestricted ? 0.5 : 1 }}
                        />
                    )}
          {((isMcqCoding || isVideoOrNotes || isProjectCoding) && !isAssignMode) && (
            <Button
              variant={isEditMode ? "primary" : "outline-primary"}
              size="sm"
              onClick={() => setIsEditMode(!isEditMode)}
              disabled={isSubtaskRestricted}
              style={{ 
                fontSize: "12px", 
                padding: "4px 12px",
                backgroundColor: isEditMode ? "#007bff" : undefined,
                borderColor: isEditMode ? "#007bff" : undefined
              }}
              title={isEditMode ? "Edit Mode" : "View Mode"}
            >
              <FaPen size={12} />
            </Button>
          )}
          <Button
            variant={subtask.actionType === ACTION_TYPE.SELECT ? "primary" : "outline-primary"}
            size="sm"
            onClick={() => handleSelectOrAssign(ACTION_TYPE.SELECT)}
            disabled={!!subtask.actionType || isSubtaskRestricted}
            style={{ fontSize: "12px", padding: "4px 12px" }}
          >
            Select
          </Button>
          <Button
            variant={subtask.actionType === ACTION_TYPE.ASSIGN ? "primary" : "outline-primary"}
            size="sm"
            onClick={() => handleSelectOrAssign(ACTION_TYPE.ASSIGN)}
            disabled={!!subtask.actionType || isSubtaskRestricted}
            style={{ fontSize: "12px", padding: "4px 12px" }}
          >
            Assign
          </Button>
          <FormControlLabel
            control={
              <Checkbox
                checked={subtask.isMandatory}
                onChange={(e) => handleMandatoryChange(e.target.checked)}
                disabled={isSubtaskRestricted}
                size="small"
              />
            }
            label={<span style={{ fontSize: "13px" }}>Mandatory</span>}
            style={{ margin: 0 }}
          />
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => onDelete({
              type: "subtask",
              id: subtask.subtask_id,
              name: subtask.name || `Subtask ${subtaskIndex + 1}`,
              phaseIndex,
              partIndex,
              taskIndex,
              subtaskIndex
            })}
            style={{ minWidth: "32px", padding: "4px 8px" }}
            disabled={isSubtaskRestricted}
          >
            <FiTrash2 size={14} />
          </Button>
        </div>
      </div>
      <Collapse in={isExpanded}>
        <div>
          {/* Only show filters in edit mode - Hide for PROJECT_CODING and MCQ/Coding in assign mode when content exists */}
          {subtask.showDropdowns && (isEditMode || !hasExistingContent || (!isMcqCoding && !isVideoOrNotes && !isProjectCoding)) && !(isProjectCoding && isAssignMode && (hasExistingContent || contentCreated)) && !(isMcqCoding && isAssignMode && (hasExistingContent || contentCreated)) && (
            <div className="mt-3 pt-3 border-top">
              <CommonFilters
                subtask={subtask}
                subjects={subjects}
                availableTopics={availableTopics}
                availableSubtopics={availableSubtopics}
                isAssignMode={isAssignMode}
                isSubtaskRestricted={isSubtaskRestricted}
                onSubjectChange={handleSubjectChange}
                onTopicChange={handleTopicChange}
                onSubtopicChange={handleSubtopicChange}
                onSubtaskTypeChange={handleSubtaskTypeChange}
              />
              <Row className="g-3 mt-0">
                <CodingEditorFilter
                  subtask={subtask}
                  isAssignMode={isAssignMode}
                  isEditMode={isEditMode}
                  hasExistingContent={hasExistingContent}
                  isSubtaskRestricted={isSubtaskRestricted}
                  onEditorChange={handleEditorChange}
                />
              </Row>
            </div>
          )}

          {/* Show content section in edit mode OR in view mode for MCQ/Coding/Video/Notes/Project Coding */}
        {(showContentSection || ((isMcqCoding || isVideoOrNotes || isProjectCoding) && (!isEditMode || (isAssignMode && (isMcqCoding || isProjectCoding))) && hasExistingContent)) && (
          <div className="mt-4 pt-2 border-top">
              {/* View Mode for MCQ/Coding with existing content - Show in view mode OR in assign mode */}
              {isMcqCoding && (!isEditMode || isAssignMode) && hasExistingContent && (
                <div>
                  <SubtopicInfoTable subtopicInfo={subtopicInfo} />
                  
                  {/* Questions - Use same UI as edit mode */}
                  {loadingViewData ? (
                    <div>Loading...</div>
                  ) : viewModeContent && viewModeContent.content ? (
                    <div>
                      {Object.keys(viewModeContent.content).map((levelKey) => {
                        const levelQuestions = viewModeContent.content[levelKey] || [];
                        return (
                          <LevelQuestionsDisplay 
                            key={levelKey}
                            levelKey={levelKey}
                            questions={levelQuestions}
                            subtask={subtask}
                            contentStatus={contentStatus || undefined}
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-muted">No data available</div>
                  )}
                </div>
              )}
              
              {/* View Mode for PROJECT_CODING with existing content */}
              {isProjectCoding && (!isEditMode || isAssignMode) && hasExistingContent && (
                <ProjectCodingView
                  subtask={subtask}
                  subtopicInfo={subtopicInfo}
                  viewModeContent={viewModeContent}
                  loadingViewData={loadingViewData}
                  contentStatus={contentStatus}
                />
              )}
              
              {/* View Mode for Video/Notes with existing content */}
              {isVideoOrNotes && !isEditMode && hasExistingContent && subtask.id && (
                <div>
                  <SubtopicInfoTable subtopicInfo={subtopicInfo} />
                  
                  {/* Video/Notes Display with Preview Button */}
                  <div className="d-flex justify-content-between mb-2 align-items-center">
                    <div className="d-flex align-items-center p-2 border rounded flex-grow-1">
                      <strong className="ms-2">
                        {subtask.subtaskType.toUpperCase()}
                      </strong>
                      {subtask.selectedContentLevel && (
                        <> - {subtask.selectedContentLevel} </>
                      )}
                      {" - "}
                      {subtask.id}
                    </div>
                    <span className="d-flex align-items-center gap-2">
                      {subtask.selectedContentTime && (
                        <strong>{subtask.selectedContentTime || "0"}m</strong>
                      )}
                      <button
                        className="border border-none rounded-2 ms-2"
                        onClick={() => {
                          const contentId = subtask.id && String(subtask.id).trim() ? String(subtask.id).trim() : null;
                          if (subtask.subtaskType === SUBTASK_TYPE.VIDEO && contentId) {
                            handleVideoPreview(contentId);
                          } else if (subtask.subtaskType === SUBTASK_TYPE.NOTES && contentId) {
                            handleNotesPreview(contentId);
                          }
                        }}
                        style={{ backgroundColor: "#F1F2FF", padding: "4px 12px" }}
                      >
                        Preview
                      </button>
                      {subtask.id && (
                        <ContentStatusIndicator
                          contentId={subtask.id}
                          contentType={subtask.subtaskType as typeof SUBTASK_TYPE[keyof typeof SUBTASK_TYPE]}
                          contentStatus={contentStatus}
                        />
                      )}
                    </span>
                  </div>
                </div>
              )}
              
              {/* Level inputs and Add Content button for MCQ/Coding in Assign Mode - Always visible */}
              {isAssignMode && (subtask.subtaskType === SUBTASK_TYPE.MCQ || subtask.subtaskType === SUBTASK_TYPE.CODING) && (
                <Row className="g-3 mb-3">
                  <Col md={3}>
                    <Form.Label>Level 1</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      placeholder="Level 1 Count"
                      value={level1Count}
                      onChange={(e) => setLevel1Count(e.target.value)}
                      disabled={loadingContent || isSubtaskRestricted}
                      size="sm"
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Label>Level 2</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      placeholder="Level 2 Count"
                      value={level2Count}
                      onChange={(e) => setLevel2Count(e.target.value)}
                      disabled={loadingContent || isSubtaskRestricted}
                      size="sm"
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Label>Level 3</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      placeholder="Level 3 Count"
                      value={level3Count}
                      onChange={(e) => setLevel3Count(e.target.value)}
                      disabled={loadingContent || isSubtaskRestricted}
                      size="sm"
                    />
                  </Col>
                  <Col md={3}>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={createBlankContent}
                      disabled={!subtask.subtopicId || loadingContent || !projectId || isSubtaskRestricted}
                      style={{ height: "40px", width: "100px" }}
                    >
                      {loadingContent ? "Creating..." : "Add Content"}
                    </Button>
                  </Col>
                </Row>
              )}

              {/* Edit Mode (Current Flow) - Show for PROJECT_CODING in assign mode even with existing content */}
              {((isEditMode || !hasExistingContent || (!isMcqCoding && !isVideoOrNotes && !isProjectCoding)) || (isProjectCoding && isAssignMode)) && (
                <>
              <LevelDropdown
                subtask={subtask}
                isAssignMode={isAssignMode}
                isSelectMode={isSelectMode}
                contentCreated={contentCreated}
                isSubtaskRestricted={isSubtaskRestricted}
                onContentLevelChange={handleContentLevelChange}
                onAddContent={handleAddContentClick}
                isAddingContent={isAddingContent}
                contentCreators={contentCreators}
                onCcChange={handleCcChange}
              />

              {isSelectMode && subtask.selectedContentLevel && !isSubtaskRestricted && (
                <div className="mt-3">
                  {loadingContent ? (
                    <div>Loading...</div>
                  ) : subtask.subtaskType === SUBTASK_TYPE.PROJECT_CODING ? (
                    <ProjectCodingEditor
                      subtask={subtask}
                      filteredContent={filteredContent}
                      contentStatus={contentStatus}
                      onContentSelected={handleContentSelected}
                    />
                  ) : (
                    <CommonContentList
                      subtask={subtask}
                      filteredContent={filteredContent}
                      contentStatus={contentStatus}
                      getSelectedIds={getSelectedIds}
                      onContentSelected={handleContentSelected}
                      onVideoPreview={handleVideoPreview}
                      onNotesPreview={handleNotesPreview}
                      availableContent={subtask.availableContent}
                    />
                  )}
                </div>
              )}

              {isAssignMode && (
                <div className="mt-3">
                  {/* Only show Created Content section for Video/Notes, not for MCQ/Coding/Project Coding (already shown in view mode above) */}
                  {((subtask.subtaskType === SUBTASK_TYPE.VIDEO || subtask.subtaskType === SUBTASK_TYPE.NOTES) && 
                    ((subtask.id && String(subtask.id).trim()) || (subtask.createdContent && Array.isArray(subtask.createdContent) && subtask.createdContent.length > 0))) && (
                    <>
                      <h6>Created Content:</h6>
                      <div className="d-flex flex-wrap gap-2">
                        {/* Display created content */}
                        {subtask.createdContent && subtask.createdContent.map((content, idx) => {
                          // Determine which level has content and get the ID(s)
                          let level: string | null = null;
                          let contentIds: any[] = [];
                          
                          if (content.count.level1 && content.count.level1.length > 0) {
                            level = "level1";
                            contentIds = content.count.level1.map((id: any) => String(id));
                          } else if (content.count.level2 && content.count.level2.length > 0) {
                            level = "level2";
                            contentIds = content.count.level2.map((id: any) => String(id));
                          } else if (content.count.level3 && content.count.level3.length > 0) {
                            level = "level3";
                            contentIds = content.count.level3.map((id: any) => String(id));
                          }
                          
                          // Display all IDs in the level
                          return (
                            <div key={idx} className="d-flex flex-column w-100 mb-2">
                              {contentIds.map((contentId, idIdx) => (
                                <div key={idIdx} className="d-flex mb-2 align-items-center">
                                  <div className="p-2 border rounded flex-grow-1">
                                    <strong className="ms-2">{content.type.toUpperCase()}</strong>
                                    <> ({contentId})</>
                                    {level && <> - {level}</>}
                                  </div>
                                  <ContentStatusIndicator
                                    contentId={contentId}
                                    contentType={subtask.subtaskType as typeof SUBTASK_TYPE[keyof typeof SUBTASK_TYPE]}
                                    contentStatus={contentStatus}
                                  />
                                </div>
                              ))}
                            </div>
                          );
                        })}
                        {/* Fallback: Display selected content for old/existing content */}
                        {(!subtask.createdContent || subtask.createdContent.length === 0) && 
                         subtask.id && String(subtask.id).trim() && (
                          <div className="d-flex mb-2 align-items-center">
                            <div className="p-2 border rounded flex-grow-1">
                              <strong>{subtask.subtaskType.toUpperCase()}</strong>
                              <> ({subtask.id})</>
                              {subtask.selectedContentLevel && <> - {subtask.selectedContentLevel}</>}
                            </div>
                            <ContentStatusIndicator
                              contentId={String(subtask.id).trim()}
                              contentType={subtask.subtaskType as typeof SUBTASK_TYPE[keyof typeof SUBTASK_TYPE]}
                              contentStatus={contentStatus}
                            />
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
                </>
              )}
            </div>
          )}
        </div>
      </Collapse>
      
      {/* Description Modal for PROJECT_CODING */}
      {isProjectCoding && (
        <Modal show={showDescriptionModal} onHide={() => setShowDescriptionModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Project coding Description</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={6}
                value={descriptionText}
                onChange={(e) => setDescriptionText(e.target.value)}
                placeholder="Enter project coding description..."
                disabled={isSubtaskRestricted}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDescriptionModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                handleDescriptionChange(descriptionText);
                setShowDescriptionModal(false);
              }}
              disabled={isSubtaskRestricted}
            >
              Save
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default SubtaskComponent;